import { CmdRangeFilterForm, CmdRangeInput } from '@commander-services/gui-components';
import * as React from 'react';
import { IntlShape, useIntl } from 'react-intl';
import { useRecoilState, useSetRecoilState } from 'recoil';
import { Controller, FieldValues, useForm } from 'react-hook-form';
import { AVARAGE_SPEED_KEY, ESCAPE_KEY, MAXIMAL_SPEED_KEY } from '../constants';
import useKeyPress from '../../../hooks/useKeyPress';
import useOutsideClick from '../../../hooks/useOutsideClick';
import { ICheckedTableItems, IFilterTripsOverview, ITripsOverviewTableData } from '../interfaces';
import {
  checkedTableItemsAtom,
  filterForRequestAtom,
  hasMoreAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFilterAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import { transformErrors } from '../../../Services/HookFormService';
import useAnalytics from '../../../hooks/useAnalytics';

interface IRangeSelectFilterValues extends FieldValues {
  from: string | number;
  to: string | number;
}

interface IRangeSelectFilterProps {
  id: string;
  title: string;
  valueFrom?: string | number;
  valueTo?: string | number;
  defaultMinValue: string | number;
  defaultMaxValue: string | number;
  suffix?: string;
  note?: string;
}

const validateValues = (
  values: IRangeSelectFilterValues,
  props: IRangeSelectFilterProps,
  f: IntlShape['formatMessage']
): Record<string, string> => {
  const errors: Record<string, string> = {};
  const convertedValues = { ...values };
  convertedValues.from = Number(String(values.from).replace(',', '.'));
  convertedValues.to = Number(String(values.to).replace(',', '.'));

  if (!values.from) {
    errors.from = `${f({ id: 'waypoints.edit.title.required' })}, ${f({
      id: 'ride.menu.speedlimit.validation',
    })}`;
  }
  if (!values.to) {
    errors.to = `${f({ id: 'waypoints.edit.title.required' })}, ${f({
      id: 'ride.menu.speedlimit.validation',
    })}`;
  }

  if (Number(convertedValues.from) < Number(props.defaultMinValue)) {
    errors.from = `${f({ id: 'tables.tripsOverview.rangeSelect.greater.than' })} ${props.defaultMinValue}`;
  }
  if (Number(convertedValues.from) >= Number(convertedValues.to)) {
    errors.from = `${f({ id: 'datepicker.spanFromToValidation' })}`;
  }
  if (
    Number(convertedValues.to) > Number(props.defaultMaxValue) &&
    Number(convertedValues.to) >= Number(props.defaultMinValue) &&
    Number(convertedValues.to) > Number(convertedValues.from)
  ) {
    errors.to = `${f({ id: 'tables.tripsOverview.rangeSelect.less.greater' })} ${props.defaultMaxValue}`;
  }

  return errors;
};

const resolver = async (data: IRangeSelectFilterValues, context) => {
  const { props, f } = context;
  const validateErrors = validateValues(data, props, f);
  const formErrors = transformErrors(validateErrors);
  return {
    values: formErrors && Object.keys(formErrors).length ? {} : data,
    errors: formErrors,
  };
};

function RangeSelectFilter(props: IRangeSelectFilterProps): JSX.Element {
  const [tableActionsFilters, setTableActionsFilters] = useRecoilState(
    rideBookFilterAtom(props.id)
  );
  const setFilterForRequest = useSetRecoilState<IFilterTripsOverview>(filterForRequestAtom);
  const { formatMessage: f } = useIntl();

  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const { trackEvent } = useAnalytics();

  const {
    handleSubmit,
    control,
    setValue,
    trigger,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      from: props.valueFrom ? props.valueFrom : props.defaultMinValue,
      to: props.valueTo ? props.valueTo : props.defaultMaxValue,
    },
    resolver: async (values) => resolver(values, { props, f }),
  });

  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();

  useOutsideClick(clickOutsideRef, () => {
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
        }
    );
  });

  useKeyPress(ESCAPE_KEY, () => {
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
        }
    );
  });

  const handleOnSubmit = (values) => {
    const data = {};
    trackEvent(['ride book', 'click', `Ride book submit range filter: ${props.id}`]);

    const convertedValues = { ...values };
    convertedValues.from = Number(String(values.from).replace(',', '.'));
    convertedValues.to = Number(String(values.to).replace(',', '.'));
    data[props.id] = { min: convertedValues.from, max: convertedValues.to };

    setOffset(0);
    setTableData(null);
    setRideItemTimestamps([]);
    setSummaryItemIndexes([]);
    setRideItemIds([]);
    setHasMore(true);
    setCheckedTableItems({
      mainCheckbox: false,
      intervals: [],
      intervalsBlacklist: [],
      rideIdBlacklist: {},
      rideIds: {},
      customerId: null,
      counts: {},
    });

    setFilterForRequest((prevFilter) => ({
      ...prevFilter,
      ...data,
    }));
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
          isFilterActive: true,
          values: {
            value: { min: Number(values.from), max: Number(values.to) },
            item: `${values.from}${props.suffix ? props.suffix : ''} - ${values.to}${
              props.suffix ? props.suffix : ''
            }`,
            title: props.title,
            type: 'rangeSelect',
            min: values.from,
            max: values.to,
            unit: props.note,
            suffixInput: props.suffix,
            defaultMin: tableActionsFilters?.data?.minValue,
            defaultMax: tableActionsFilters?.data?.maxValue,
          },
        }
    );
  };

  const handleReset = () => {
    trackEvent(['ride book', 'click', `Ride book reset range filter: ${props.id}`]);
    setValue('from', props.defaultMinValue);
    setValue('to', props.defaultMaxValue);
    trigger();
  };

  return (
    <div className="table-filter" ref={clickOutsideRef}>
      {tableActionsFilters && tableActionsFilters.data && (
        <CmdRangeFilterForm
          id={props.id}
          submitCallback={handleSubmit(handleOnSubmit)}
          closeCallback={handleReset}
          submitBtnTitle={f({ id: 'table.apply' })}
        >
          <Controller
            name="from"
            control={control}
            render={({ field }) => (
              <CmdRangeInput
                {...field}
                id="from"
                label={f({ id: 'table_list.columnFilters.rangeSelect.min' })}
                type="float"
                currency={props.suffix}
                setValue={(value) => {
                  setValue('from', value);
                  trigger();
                }}
                note={props.note}
                noDecimal={!!(props.id === MAXIMAL_SPEED_KEY || props.id === AVARAGE_SPEED_KEY)}
                error={errors?.from?.message}
                touched={true}
              />
            )}
          />
          <Controller
            name="to"
            control={control}
            render={({ field }) => (
              <CmdRangeInput
                {...field}
                id="to"
                name="to"
                label={f({ id: 'table_list.columnFilters.rangeSelect.max' })}
                type="float"
                currency={props.suffix}
                setValue={(value) => {
                  setValue('to', value);
                  trigger();
                }}
                note={props.note}
                margin={true}
                noDecimal={!!(props.id === MAXIMAL_SPEED_KEY || props.id === AVARAGE_SPEED_KEY)}
                error={errors?.to?.message}
                touched={true}
              />
            )}
          />
        </CmdRangeFilterForm>
      )}
    </div>
  );
}

export default RangeSelectFilter;
